import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { portalApi } from '@services/portal.service'
import {
  PortalMessageCreate,
  PortalPrescriptionRefillRequest,
} from '@/types'

const PORTAL_KEY = 'portal'

export function usePortalDashboard() {
  return useQuery({
    queryKey: [PORTAL_KEY, 'dashboard'],
    queryFn: () => portalApi.getDashboard(),
  })
}

export function usePortalMedicalRecords() {
  return useQuery({
    queryKey: [PORTAL_KEY, 'medical-records'],
    queryFn: () => portalApi.getMedicalRecords(),
  })
}

export function usePortalMessages(folder = 'inbox', page = 1, pageSize = 20) {
  return useQuery({
    queryKey: [PORTAL_KEY, 'messages', folder, page, pageSize],
    queryFn: () => portalApi.getMessages(folder, page, pageSize),
  })
}

export function usePortalMessage(messageId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [PORTAL_KEY, 'message', messageId],
    queryFn: () => portalApi.getMessage(messageId),
    enabled: options?.enabled ?? !!messageId,
  })
}

export function useSendPortalMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: PortalMessageCreate) => portalApi.sendMessage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PORTAL_KEY, 'messages'] })
    },
  })
}

export function usePortalAppointments() {
  return useQuery({
    queryKey: [PORTAL_KEY, 'appointments'],
    queryFn: () => portalApi.getAppointments(),
  })
}

export function usePortalInvoices() {
  return useQuery({
    queryKey: [PORTAL_KEY, 'invoices'],
    queryFn: () => portalApi.getInvoices(),
  })
}

export function usePortalPrescriptions() {
  return useQuery({
    queryKey: [PORTAL_KEY, 'prescriptions'],
    queryFn: () => portalApi.getPrescriptions(),
  })
}

export function useRequestPrescriptionRefill() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: PortalPrescriptionRefillRequest) =>
      portalApi.requestRefill(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PORTAL_KEY, 'prescriptions'] })
    },
  })
}

export function usePortalAvailableSlots(dateFrom: string, dateTo: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [PORTAL_KEY, 'available-slots', dateFrom, dateTo],
    queryFn: () => portalApi.getAvailableSlots(dateFrom, dateTo),
    enabled: options?.enabled ?? !!(dateFrom && dateTo),
  })
}

export function useRequestAppointment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: {
      preferred_date: string
      preferred_time?: string
      appointment_type: string
      reason?: string
      preferred_doctor_id?: string
      is_urgent: boolean
    }) => portalApi.requestAppointment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PORTAL_KEY, 'appointments'] })
      queryClient.invalidateQueries({ queryKey: [PORTAL_KEY, 'dashboard'] })
    },
  })
}
